import type { Row, Table } from '@tanstack/react-table';
import * as React from 'react';

import { Box } from '../../components/ui/box';
import { Checkbox } from '../../components/ui/checkbox';
import { useDataTableGroupRows, useDataTableShiftSelect, useDataTableSelectAll } from './data-table-context';
import type { DataTableColumnDef } from './data-table-types';
import { SELECTION_COLUMN_WIDTH } from './data-table-utils';

// ---------------------------------------------------------------------------
// Global shift-key tracking
//
// Shift-range selection needs the modifier state at the moment a checkbox
// TOGGLES, and `onCheckedChange` carries no originating mouse event, so the
// key state is tracked document-wide instead of read off the click.
//
// Registration is LAZY and idempotent. This module is reachable from the
// package barrel, so a bare `document.addEventListener` at module scope runs
// on import — and throws `document is not defined` in every non-DOM consumer
// (SSR, a node-environment test suite) before a single component mounts.
// Nothing in this package may touch `document` at import time.
// ---------------------------------------------------------------------------

let shiftKeyDown = false;
let trackingShiftKey = false;

function trackShiftKey(): void {
  if (trackingShiftKey || typeof document === "undefined") return;
  trackingShiftKey = true;
  document.addEventListener("keydown", (e) => {
    if (e.key === "Shift") shiftKeyDown = true;
  });
  document.addEventListener("keyup", (e) => {
    if (e.key === "Shift") shiftKeyDown = false;
  });
}

/**
 * Selection header checkbox component.
 * When rendered inside a group context, scopes select-all to that group's rows.
 * Otherwise falls back to all page rows.
 *
 * Supports three states:
 * - Unchecked: No rows selected
 * - Indeterminate: Some rows selected
 * - Checked: All rows selected
 */
function SelectionHeader<TData>({ table }: { table: Table<TData> }) {
  "use no memo";

  const groupRows = useDataTableGroupRows<TData>();
  const { isSelectAll, setSelectAll } = useDataTableSelectAll();

  let isAllSelected: boolean;
  let isSomeSelected: boolean;
  let handleToggle: (value: boolean) => void;

  // Global selection count includes off-page selections that survive page eviction
  const globalSelectedCount = Object.values(table.getState().rowSelection).filter(Boolean).length;

  // Only selectable rows participate — rows the viewer can't act on have no
  // checkbox, so they neither count toward "all selected" nor get toggled.
  const selectableGroupRows = groupRows?.filter((r) => r.getCanSelect());

  // When a group has loaded rows but none are selectable, omit the select-all
  // checkbox entirely — there's nothing to select.
  if (groupRows && groupRows.length > 0 && selectableGroupRows!.length === 0) {
    return null;
  }

  if (selectableGroupRows && selectableGroupRows.length > 0) {
    // Group-scoped selection
    const visibleSelectedCount = selectableGroupRows.filter((r) => r.getIsSelected()).length;
    isAllSelected = isSelectAll || (visibleSelectedCount === selectableGroupRows.length && globalSelectedCount === visibleSelectedCount);
    isSomeSelected = globalSelectedCount > 0 && !isAllSelected;
    handleToggle = (value: boolean) => {
      if (!value) {
        // Clear all selections including off-page rows
        table.setRowSelection({});
        setSelectAll(false);
      } else {
        table.setRowSelection((old) => {
          const next = { ...old };
          for (const row of selectableGroupRows) {
            next[row.id] = true;
          }
          return next;
        });
        setSelectAll(true);
      }
    };
  } else {
    // Fallback: all page rows
    isAllSelected = isSelectAll || (table.getIsAllPageRowsSelected() && globalSelectedCount === table.getRowModel().rows.length);
    isSomeSelected = globalSelectedCount > 0 && !isAllSelected;
    handleToggle = (value: boolean) => {
      if (!value) {
        table.setRowSelection({});
        setSelectAll(false);
      } else {
        table.toggleAllPageRowsSelected(true);
        setSelectAll(true);
      }
    };
  }

  const hideUntilHover = !isAllSelected && !isSomeSelected;

  return (
    <Box
      width={16}
      height={16}
      align="center"
      justify="center"
      className={hideUntilHover ? 'opacity-100 md:opacity-0 md:group-hover/header:opacity-100' : undefined}
    >
      <Checkbox
        checked={isAllSelected}
        indeterminate={isSomeSelected}
        onCheckedChange={(value) => handleToggle(!!value)}
        aria-label={
          isAllSelected
            ? 'Deselect all rows'
            : isSomeSelected
              ? 'Select all rows (some selected)'
              : 'Select all rows'
        }
      />
    </Box>
  );
}

/**
 * Selection cell checkbox component.
 * Handles individual row selection.
 *
 * Uses `table.setRowSelection()` directly instead of `row.toggleSelected()`
 * to avoid TanStack's internal `getRow()` lookup — cached rows from
 * off-screen pages aren't in the current row model and would throw.
 */
function SelectionCell<TData>({ row, table }: { row: Row<TData>; table: Table<TData> }) {
  "use no memo";

  // Arm the document-wide shift tracking on first mount of any selection cell.
  React.useEffect(trackShiftKey, []);

  const canSelect = row.getCanSelect();
  const rowSelected = row.getIsSelected();

  // Non-selectable rows render no checkbox at all — the viewer can't act on
  // this row, so the selection affordance is omitted entirely (not disabled).
  // Hooks above this point keep the call order stable across rows.
  const omitCheckbox = !canSelect;
  const groupRows = useDataTableGroupRows<TData>();
  const lastSelectedIdRef = useDataTableShiftSelect();
  const { isSelectAll, setSelectAll } = useDataTableSelectAll();

  // row.getIsSelected() is false for off-page rows evicted from the row model;
  // isSelectAll covers those so the checkbox stays checked.
  const isSelected = rowSelected || isSelectAll;
  const hideUntilHover = !isSelected;

  const handleSelectionChange = React.useCallback(
    (value: boolean) => {
      // Deselecting a row breaks the "select all" state for this group.
      // Individually select all other loaded rows so they stay checked.
      if (!value && isSelectAll) {
        setSelectAll(false);
        if (groupRows) {
          table.setRowSelection((old) => {
            const next = { ...old };
            for (const r of groupRows) {
              if (r.id !== row.id) next[r.id] = true;
            }
            delete next[row.id];
            return next;
          });
          if (lastSelectedIdRef) lastSelectedIdRef.current = row.id;
          return;
        }
      }

      if (shiftKeyDown && lastSelectedIdRef?.current != null && groupRows) {
        if (row.getIsSelected()) {
          // Shift-clicking an already selected row just deselects it
          table.setRowSelection((old) => {
            const next = { ...old };
            delete next[row.id];
            return next;
          });
        } else {
          const lastIdx = groupRows.findIndex(
            (r) => r.id === lastSelectedIdRef.current,
          );
          const currentIdx = groupRows.findIndex((r) => r.id === row.id);

          if (lastIdx !== -1 && currentIdx !== -1) {
            const start = Math.min(lastIdx, currentIdx);
            const end = Math.max(lastIdx, currentIdx);
            table.setRowSelection((old) => {
              const next = { ...old };
              for (const row of groupRows.slice(start, end + 1)) {
                next[row.id] = true;
              }
              return next;
            });
          } else {
            // Anchor row is on a different page — fall back to normal toggle
            // and reset anchor to this row
            table.setRowSelection((old) => ({ ...old, [row.id]: true }));
            lastSelectedIdRef.current = row.id;
          }
        }
      } else {
        table.setRowSelection((old) => {
          const next = { ...old };
          if (value) {
            next[row.id] = true;
          } else {
            delete next[row.id];
          }
          return next;
        });
      }

      // Update the anchor on non-shift clicks, or when there's no anchor yet,
      // so the user can shift-click multiple times from the same starting row.
      if (lastSelectedIdRef && (!shiftKeyDown || lastSelectedIdRef.current == null)) {
        lastSelectedIdRef.current = row.id;
      }
    },
    [groupRows, lastSelectedIdRef, row, table, isSelectAll, setSelectAll],
  );

  // Render an empty cell (no checkbox) when the row isn't selectable.
  if (omitCheckbox) {
    return <Box alignSelf="stretch" fill className="h-full" />;
  }

  return (
    <Box alignSelf="stretch"
      fill
      direction="row"
      align="center"
      justify="center"
      className="h-full"
      onClick={() => handleSelectionChange(!isSelected)}
    >
      <Box
        width={16}
        height={16}
        align="center"
        justify="center"
        className={hideUntilHover ? 'opacity-100 md:opacity-0 md:group-hover/row:opacity-100' : undefined}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={handleSelectionChange}
          aria-label="Select row"
        />
      </Box>
    </Box>
  );
}

/**
 * Creates a selection column definition for row selection.
 *
 * The header checkbox toggles all visible rows (current page when paginated).
 *
 * @example
 * ```tsx
 * const columns: DataTableColumnDef<Payment>[] = [
 *   createSelectionColumn<Payment>(),
 *   { accessorKey: 'email', header: 'Email' },
 * ];
 * ```
 */
export function createSelectionColumn<TData>(): DataTableColumnDef<TData, unknown> {
  const columnDef: DataTableColumnDef<TData, unknown> = {
    id: 'select',
    header: ({ table }) => <SelectionHeader table={table} />,
    cell: ({ row, table }) => <SelectionCell row={row} table={table} />,
    enableSorting: false,
    enableHiding: false,
    meta: {
      width: SELECTION_COLUMN_WIDTH,
    },
  };
  return columnDef;
}
